/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useANCInvestigations } from "./hooks/useANCInvestigations";
import ANCInvestigationPanel from "./ANCInvestigationPanel";
import { ANCOrderDialog, ANCResultDialog } from "./ANCInvestigationForm";
import { history } from "../../../../Helpers/history";
import {
    InvestPageWrapper,
    InvestHeader,
    InvestHeaderTitle,
    InvestHeaderStats,
    StatBadge,
    EmptyState,
    GoToBookingBtn,
    LoadingWrapper,
} from "./ANCInvestigationStyles";

/**
 * ANCInvestigations — Page-level container for ANC Screening & Investigations
 *
 * Route: /gyn-and-obs-anc-investigations/:patient_number/:visit_id/:patient_type
 */
const ANCInvestigations = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── Investigations hook ─── */
    const inv = useANCInvestigations(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    return (
        <div className="journals">
            <PageTitle title="ANC Screening & Investigations" />
            <SubHeader
                title="OB/GYN"
                subTitle="ANC Screening & Investigations"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <InvestPageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {inv.loadingBooking && (
                        <LoadingWrapper>
                            <span>⏳</span> Loading patient ANC data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!inv.loadingBooking && !inv.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            <EmptyState>
                                <div className="empty-icon">🧪</div>
                                <div className="empty-title">No Active ANC Booking Found</div>
                                <div className="empty-desc">
                                    This patient must be registered in the Antenatal Care program
                                    before investigations can be ordered. The ANC booking links all
                                    screening tests to the current pregnancy.
                                </div>
                                <GoToBookingBtn onClick={goToBooking}>
                                    Go to ANC Registration & Booking
                                </GoToBookingBtn>
                            </EmptyState>
                        </div>
                    )}

                    {/* ═══ MAIN CONTENT ═══ */}
                    {!inv.loadingBooking && inv.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            {/* ─── Header ─── */}
                            <InvestHeader>
                                <InvestHeaderTitle>
                                    <h3>🧪 ANC Screening & Investigations</h3>
                                    <p>
                                        Track all recommended ANC tests per MOH guidelines
                                        {inv.gestationalAge ? " · GA: " + inv.gestationalAge + " weeks" : ""}
                                    </p>
                                </InvestHeaderTitle>
                                <InvestHeaderStats>
                                    <StatBadge>
                                        ✓ Resulted <span className="count">{inv.summary.resulted}</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ⏱ Pending <span className="count">{inv.summary.pending}</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ○ Not Ordered <span className="count">{inv.summary.notOrdered}</span>
                                    </StatBadge>
                                    {inv.summary.abnormal > 0 && (
                                        <StatBadge style={{ background: "rgba(229,57,53,0.3)" }}>
                                            ⚠ Abnormal <span className="count">{inv.summary.abnormal}</span>
                                        </StatBadge>
                                    )}
                                </InvestHeaderStats>
                            </InvestHeader>

                            {/* ─── Investigation Panel ─── */}
                            <ANCInvestigationPanel
                                filteredPanel={inv.filteredPanel}
                                summary={inv.summary}
                                loading={inv.loadingInvestigations}
                                gestationalAge={inv.gestationalAge}
                                onOrder={inv.openOrderForm}
                                onRecordResult={inv.openResultForm}
                                onQuickOrder={inv.quickOrderPanel}
                                saving={inv.saving}
                                activeView={inv.activeView}
                                setActiveView={inv.setActiveView}
                                filterCategory={inv.filterCategory}
                                setFilterCategory={inv.setFilterCategory}
                            />

                            {/* ─── Order Dialog ─── */}
                            <ANCOrderDialog
                                open={inv.orderForm.open}
                                investigationType={inv.orderForm.investigationType}
                                formData={inv.formData}
                                onFormChange={inv.handleFormChange}
                                onSubmit={inv.submitOrder}
                                onClose={inv.closeOrderForm}
                                saving={inv.saving}
                            />

                            {/* ─── Result Dialog ─── */}
                            <ANCResultDialog
                                open={inv.resultForm.open}
                                investigation={inv.resultForm.investigation}
                                formData={inv.formData}
                                onFormChange={inv.handleFormChange}
                                onSubmit={inv.submitResult}
                                onClose={inv.closeResultForm}
                                saving={inv.saving}
                            />
                        </div>
                     )}
                </InvestPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ANCInvestigations);
